import { styleMapper } from "../styles";
import type {
  ApiRef,
  DeforestationIncidentCardProperties,
  FireIncidentCardProperties,
  GdacsIncidentCardProperties,
  IncidentCardData,
  IncidentCardProperties,
} from "../../types";
import { compact } from "lodash";
import {
  getConfidenceLabel,
  getDayNightLabel,
  formatNumber,
  joinSentences,
  normaliseAffectedCountries,
  formatList,
  createLinks,
  createLink,
  formatDate,
  formatDateTime,
} from "./formatting";

const formatDateRange = (
  startTime: number,
  currentTime: number,
  ongoing: boolean,
): string | null => {
  const formattedStartTime = formatDateTime(startTime);
  const formattedCurrentTime = formatDateTime(currentTime);

  if (
    formattedStartTime &&
    formattedCurrentTime &&
    formattedStartTime !== formattedCurrentTime
  ) {
    if (ongoing)
      return `This incident has been active since ${formattedStartTime} and remains ongoing as of ${formattedCurrentTime}.`;

    return `This incident was recorded between ${formattedStartTime} and ${formattedCurrentTime}.`;
  }

  if (formattedStartTime) {
    if (ongoing)
      return `This incident has been active since ${formattedStartTime}.`;

    return `This incident was recorded on ${formattedStartTime}.`;
  }

  if (ongoing) return "This incident is ongoing.";

  return null;
};

const createFallbackParagraph = (): Array<string> => [
  "Limited source data is available for this incident.",
];

export const createFireCardData = (
  properties: FireIncidentCardProperties,
): IncidentCardData => {
  const titleColour = styleMapper[properties.incident].colour;
  const confidenceLabel = getConfidenceLabel(properties.confidence);
  const dayNightLabel = getDayNightLabel(properties.dayNight);
  const overPassTimeUTC = properties.acquisitionDetails?.overPassTimeUTC;
  const radiativePower = formatNumber(properties.strength?.radiativePower);
  const ti4Brightness = formatNumber(properties.strength?.ti4Brightness);
  const ti5Brightness = formatNumber(properties.strength?.ti5Brightness);

  const paragraphs = compact([
    joinSentences([
      "An active fire detection has been recorded for this location.",
      formatDateRange(
        properties.startTime,
        properties.currentTime,
        properties.ongoing,
      ),
    ]),
    joinSentences([
      confidenceLabel ? `Detection confidence is ${confidenceLabel}.` : null,
      dayNightLabel
        ? `The observation was captured during a ${dayNightLabel} overpass.`
        : null,
      overPassTimeUTC
        ? `The recorded overpass time was ${formatDateTime(overPassTimeUTC)}`
        : null,
      properties.acquisitionDetails?.satellite
        ? `The source satellite was ${properties.acquisitionDetails.satellite}.`
        : null,
      properties.acquisitionDetails?.instrument
        ? `The instrument used was ${properties.acquisitionDetails.instrument}.`
        : null,
    ]),
    joinSentences([
      radiativePower
        ? `Estimated fire radiative power is ${radiativePower} MW.`
        : null,
      ti4Brightness ? `TI4 brightness is ${ti4Brightness}.` : null,
      ti5Brightness ? `TI5 brightness is ${ti5Brightness}.` : null,
    ]),
  ]);

  return {
    title: properties.incident,
    titleColour,
    paragraphs: paragraphs.length ? paragraphs : createFallbackParagraph(),
    links: [],
  };
};

export const createDeforestationCardData = (
  properties: DeforestationIncidentCardProperties,
): IncidentCardData => {
  const titleColour = styleMapper[properties.incident].colour;
  const totalAreaHa = formatNumber(properties.totalAreaHa);
  const intensity = formatNumber(properties.intensity);
  const acquisitionDate = formatDate(properties.acquisitionDetails?.dateUTC);

  const paragraphs = compact([
    joinSentences([
      "Deforestation activity has been detected in this area.",
      totalAreaHa
        ? `The affected area is estimated at ${totalAreaHa} hectares.`
        : null,
      formatDateRange(
        properties.startTime,
        properties.currentTime,
        properties.ongoing,
      ),
    ]),
    joinSentences([
      acquisitionDate ? `This alert was recorded on ${acquisitionDate}.` : null,
      intensity ? `Alert intensity is recorded as ${intensity}.` : null,
    ]),
  ]);

  return {
    title: properties.incident,
    titleColour,
    paragraphs: paragraphs.length ? paragraphs : createFallbackParagraph(),
    links: [],
  };
};

export const createGdacsCardData = (
  properties: GdacsIncidentCardProperties,
): IncidentCardData => {
  const titleColour = styleMapper[properties.incident].colour;
  const affectedCountries = normaliseAffectedCountries(
    properties.affectedCountries,
  );
  const severity = formatNumber(properties.severityData?.severity);
  const firstRecorded = formatDateTime(properties.firstRecorded);
  const lastRecorded = formatDateTime(properties.lastRecorded);

  const paragraphs = compact([
    joinSentences([
      properties.eventName
        ? `${properties.eventName} has been identified for this incident.`
        : null,
      properties.webDescriptiveName ? `${properties.webDescriptiveName}` : null,
      properties.isCurrent ? "This event is currently active." : null,
      !properties.isCurrent && properties.ongoing
        ? "This incident remains ongoing."
        : null,
    ]),
    joinSentences([
      properties.alertLevel
        ? `The current GDACS alert level is ${properties.alertLevel.toLowerCase()}.`
        : null,
      properties.episodeAlertLevel
        ? `The episode alert level is ${properties.episodeAlertLevel.toLowerCase()}.`
        : null,
      severity &&
      properties.severityData?.severityText &&
      properties.severityData?.severityUnit
        ? `Severity is reported as ${severity} ${properties.severityData.severityUnit} for ${properties.severityData.severityText.toLowerCase()}.`
        : null,
      severity &&
      properties.severityData?.severityUnit &&
      !properties.severityData?.severityText
        ? `Severity is reported as ${severity} ${properties.severityData.severityUnit}.`
        : null,
    ]),
    joinSentences([
      affectedCountries.length
        ? `Affected countries include ${formatList(affectedCountries)}.`
        : null,
      properties.description ? properties.description.trim() : null,
    ]),
    joinSentences([
      firstRecorded && lastRecorded && firstRecorded !== lastRecorded
        ? `The event was first recorded on ${firstRecorded} and last updated on ${lastRecorded}.`
        : null,
      firstRecorded && (!lastRecorded || firstRecorded === lastRecorded)
        ? `The event was recorded on ${firstRecorded}.`
        : null,
      properties.isTemporary ? "This event is marked as temporary." : null,
    ]),
  ]);

  const links = createLinks([
    createLink("Report", properties.url?.report),
    createLink("Details", properties.url?.details),
    createLink("Geometry", properties.url?.geometry),
  ]);

  return {
    title: properties.incident,
    titleColour,
    paragraphs: paragraphs.length ? paragraphs : createFallbackParagraph(),
    links,
  };
};

export const createCardData = (
  properties: IncidentCardProperties,
  apiRef: ApiRef | null,
): IncidentCardData => {
  if (apiRef === "FIRMS")
    return createFireCardData(properties as FireIncidentCardProperties);
  if (apiRef === "GFW")
    return createDeforestationCardData(
      properties as DeforestationIncidentCardProperties,
    );

  return createGdacsCardData(properties as GdacsIncidentCardProperties);
};
